
import { useEffect, useRef } from "react";
import { useNotify } from "@/hooks/useNotify";
import { supabase } from "@/integrations/supabase/client";
import { AuthStateType } from "../types/auth-state";
import { instrumentAuthSignOut, trackStorageEvents } from "@/utils/authSpy";
import { tabCoordinator } from "@/utils/tabCoordinator";
import { wasInactivitySignout, isForcedReauthRequired } from "@/utils/sessionTimeout";
import { getLastKeepAliveTime } from "@/utils/authKeepAlive";
import "@/utils/debugAuthConflicts";

export function useAuthSessionInit(authState: AuthStateType) {
  const notify = useNotify();
  const { setSession, setUser, setIsLoading } = authState;
  const initializationRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const isImpersonatingRef = useRef(false);

  useEffect(() => {
    isImpersonatingRef.current = authState.isImpersonating;
  }, [authState.isImpersonating]);

  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) return;
    initializationRef.current = true;

    let mounted = true;

    const initializeAuth = async () => {
      if (!mounted) return;
      
      setIsLoading(true);
      
      try {
        // Get initial session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          notify.error(error.message || "Error fetching session");
        }

        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);
        }
      } catch (err) {
        console.error("Session fetch error:", err);
        if (mounted) {
          notify.error("Failed to initialize session. Please refresh the page.");
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Set up auth state listener with enhanced tracking and tab coordination
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      
      // Enhanced logging for all auth events with tab context
      const tabInfo = {
        tabId: tabCoordinator.getCurrentTabId(),
        shouldHandleAuth: tabCoordinator.shouldHandleAuth(),
        isInJoinFlow: tabCoordinator.isInJoinFlowContext(),
        activeTabs: tabCoordinator.getActiveTabsInfo().length
      };
      
      console.log('[Auth] State change event:', event, 'Session exists:', !!session, {
        timestamp: new Date().toISOString(),
        userId: session?.user?.id,
        userEmail: session?.user?.email,
        currentPath: window.location.pathname,
        sessionAccessToken: session?.access_token ? 'present' : 'missing',
        sessionRefreshToken: session?.refresh_token ? 'present' : 'missing',
        ...tabInfo
      });
      
      // Handle token refresh events gracefully
      if (event === 'TOKEN_REFRESHED') {
        isRefreshingRef.current = false;
        console.log('[Auth] Token refreshed successfully');
      }
      
      // Enhanced SIGNED_OUT logging with cross-tab protection and attribution
      if (event === 'SIGNED_OUT') {
        const stack = new Error().stack;
        
        // Determine sign-out cause
        let signoutCause = 'unknown';
        if (wasInactivitySignout()) {
          signoutCause = 'app-driven-48h-inactivity';
        } else if (isForcedReauthRequired()) {
          signoutCause = 'app-driven-14d-forced-reauth';
        } else {
          signoutCause = 'server-initiated-or-user-action';
        }
        
        console.log('[Auth] SIGNED_OUT event detected!', {
          timestamp: new Date().toISOString(),
          currentPath: window.location.pathname,
          wasRefreshing: isRefreshingRef.current,
          signoutCause,
          lastKeepAliveTime: getLastKeepAliveTime() 
            ? new Date(getLastKeepAliveTime()!).toISOString() 
            : 'never',
          stackTrace: stack?.split('\n').slice(1, 8), // First 8 lines
          ...tabInfo
        });
        
        // Check if this sign-out might be from cross-tab interference
        if (tabInfo.activeTabs > 1 && !tabInfo.shouldHandleAuth) {
          console.warn('[Auth] POTENTIAL CROSS-TAB SIGN-OUT DETECTED!', {
            ...tabInfo,
            message: 'Sign-out event from inactive tab - this might be unintended'
          });
        }
        
        console.log('[Auth] User signed out, resetting auth state');
        authState.setProfile(null);
        authState.setIsSuperuser(false);
        authState.setIsAdmin(false);
        isRefreshingRef.current = false;
      }
      
      // Handle SIGNED_IN events with cross-tab awareness
      if (event === 'SIGNED_IN') {
        console.log('[Auth] SIGNED_IN event with tab coordination:', tabInfo);
        
        // If we're in a join flow context and there are multiple tabs,
        // be extra careful about auth state handling
        if (tabInfo.isInJoinFlow && tabInfo.activeTabs > 1) {
          console.log('[Auth] Join flow sign-in detected with multiple tabs - monitoring for conflicts');
        }
      }
      
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[Auth] PASSWORD_RECOVERY event detected - storing recovery session state');
        authState.setIsRecoverySession(true);
      }

      // Update session and user for all events
      setSession(session);
      // Only reset user if not impersonating — preserve impersonated user object
      if (!isImpersonatingRef.current) {
        setUser(session?.user ?? null);
      }
    });

    // Initialize auth spy and storage tracking
    instrumentAuthSignOut();
    trackStorageEvents();
    
    // Initialize auth
    initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []); // Empty dependency array - runs only once
}
